export type AudienceCriterion = { dimension: string; value: string };

export type IcpMatrixRow = {
  key: string;
  label: string;
  value: string;
  source?: string | null;
};

export type CampaignAudience = {
  product?: string | null;
  description?: string | null;
  criteria?: AudienceCriterion[] | null;
  matrix?: IcpMatrixRow[] | null;
};

export const EMPTY_ICP_MATRIX: IcpMatrixRow[] = [
  { key: 'problem', label: 'Problem / job to be done', value: '' },
  { key: 'offer', label: 'What you sell them', value: '' },
  { key: 'buyer', label: 'Buyer', value: '' },
  { key: 'company', label: 'Company profile', value: '' },
  { key: 'employees', label: 'Employee count', value: '' },
  { key: 'revenue', label: 'Annual revenue', value: '' },
  { key: 'budget', label: 'Budget they can pay', value: '' },
  { key: 'geography', label: 'Geography', value: '' },
  { key: 'triggers', label: 'Buying triggers', value: '' },
  { key: 'exclusions', label: 'Disqualifiers', value: '' },
  { key: 'competitors', label: 'Competitors and alternatives', value: '' },
  { key: 'practices', label: 'Industry practices', value: '' },
];

export const icpMatrixRows = (audience?: CampaignAudience | null): IcpMatrixRow[] => {
  const existing = (audience?.matrix || []).filter((row) => row && (row.key || row.label));
  const byKey = new Map(existing.map((row) => [row.key, row]));
  const seen = new Set<string>();
  const merged = EMPTY_ICP_MATRIX.map((blank) => {
    seen.add(blank.key);
    const row = byKey.get(blank.key);
    return {
      key: blank.key,
      label: row?.label || blank.label,
      value: row?.value || '',
      source: row?.source || '',
    };
  });
  existing.forEach((row) => {
    const key = String(row.key || '').trim();
    if (!key || seen.has(key)) return;
    merged.push({
      key,
      label: row.label || key,
      value: row.value || '',
      source: row.source || '',
    });
    seen.add(key);
  });
  return merged;
};

export const icpMatrixHasValues = (audience?: CampaignAudience | null): boolean =>
  icpMatrixRows(audience).some((row) => String(row.value || '').trim());

export const audienceHasIcp = (audience?: CampaignAudience | null): boolean => {
  if (String(audience?.description || '').trim()) return true;
  if (icpMatrixHasValues(audience)) return true;
  return Boolean((audience?.criteria || []).some((row) => row.dimension && row.value));
};

export const summarizeIcp = (audience?: CampaignAudience | null, spanish = false): string => {
  if (icpMatrixHasValues(audience)) {
    return spanish ? 'ICP listo para buscar leads' : 'ICP ready — search for leads';
  }
  if (String(audience?.description || '').trim()) {
    return spanish ? 'Hay una descripción — arme la matriz' : 'Has a description — build the matrix';
  }
  if ((audience?.criteria || []).some((row) => row.dimension && row.value)) {
    return spanish ? 'Reemplace los filtros antiguos con una descripción' : 'Replace old filters with a description';
  }
  return spanish ? 'Sin ICP todavía' : 'No ICP set yet';
};
